import {
  AddTask,
  Cancel,
  ContentCopy as ContentCopyIcon,
  Inventory as InventoryIcon,
  PublishedWithChanges,
  Replay,
  SwapHoriz,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import clsx from "clsx";
import { FC, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { AccessControl, FramerTooltip, MuiButton } from "@ryde/controls";
import {
  useCrudEditContext,
  useRideEmptyStationsAllowed,
  useSupplierProvider,
} from "@ryde/redux";
import {
  editFormDataInitialSnapshotAF,
  IRideDto,
  isDateTimeInPast,
  isInnerItemEditingAtom,
  isPastTaxiSupplierRide,
  isTaxiProvider,
  loadersEditFormWithApiDataSF,
  Log,
  NO_VALUE_DISPLAY,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  RideFormFields,
  RideStationPanelMode,
  RideStatusEnum,
  useHaveCustomPermissions,
  useSystemParamsForSite,
} from "@ryde/shared";

import { EditFormFooterButton } from "../../../../right";
import { useRideFooterActions } from "../hooks";
import {
  RideFooterLeftButtonsStyled,
  RideFooterRightButtonsStyled,
  RideFooterStyled,
} from "./ride-footer.styled";

export const RideFooter: FC = () => {
  const { t } = useTranslation();
  const {
    data: { isEdit, dto: rideDto },
  } = useCrudEditContext<IRideDto>();
  const {
    onArchiveClickEvent,
    onConvertToDynamicRideClick,
    onDuplicateClickEvent,
    onSaveAndStayClickEvent,
    onSaveAndSendToSupplierHandler,
    onCancelledClickEvent,
    onRenewRidesClickEvent,
    onDuplicateRevertedRideClick,
    onDuplicateToNewSupplierClick,
  } = useRideFooterActions();
  const { allowSendingToSupplierWithoutCompanyId } = useSystemParamsForSite(
    rideDto?.siteId,
  );

  const rideId = rideDto?.id;
  const hasCourseId = useMemo(() => {
    return Boolean(rideDto?.courseId);
  }, [rideDto?.courseId]);
  const isLoaderActive = useRecoilValue(
    loadersEditFormWithApiDataSF(RideCrudModeKey.uiUnit),
  );
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);

  const rideInitialSnapshotDto = useRecoilValue<IRideDto>(
    editFormDataInitialSnapshotAF(RideCrudModeKeyTuple),
  );
  const supplierId = useWatch({ name: RideFormFields.supplierId });
  const vehicleTypeId = useWatch({ name: RideFormFields.vehicleTypeId });
  const watchedRideStart = useWatch({ name: RideFormFields.rideStart });

  // RYDE-7633: block Send when the CURRENTLY selected supplier is a taxi provider and the ride start
  // is in the past — a taxi provider rejects a past ride. Keyed on the live selection (not the
  // dispatched provider) so switching to a past-allowed supplier re-enables Send, and switching to a
  // taxi one disables it, without ever locking the supplier dropdown.
  const {
    provider: selectedSupplierProvider,
    isResolved: isSelectedSupplierProviderResolved,
  } = useSupplierProvider(supplierId);
  const isSendBlockedForSelectedTaxiPast = useMemo(() => {
    if (!isDateTimeInPast(watchedRideStart ?? rideDto?.rideStart)) {
      return false;
    }

    // The provider is read from the supplier dropdown source asynchronously; while it is unresolved
    // stay blocked, otherwise Send is briefly clickable for a past ride whose selected supplier turns
    // out to be a taxi provider.
    return (
      !isSelectedSupplierProviderResolved ||
      isTaxiProvider(selectedSupplierProvider)
    );
  }, [
    selectedSupplierProvider,
    isSelectedSupplierProviderResolved,
    watchedRideStart,
    rideDto?.rideStart,
  ]);

  const {
    hasRideUpdatePermission,
    hasRideUpdateDriverPermission,
    hasRideUpdateVehiclePermission,
    hasEditNewOnlyRidePermission,
  } = useHaveCustomPermissions();

  const { isRideWithEmptyStationsAllowed } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDto,
  );
  const hasEditNewOnlyRideRestriction = useMemo(
    () =>
      hasEditNewOnlyRidePermission && rideDto?.status !== RideStatusEnum.New,
    [hasEditNewOnlyRidePermission, rideDto?.status],
  );

  const isSpecialCaseLastPassengerRemoved =
    !rideDto?.metadata?.stationIds?.length &&
    rideInitialSnapshotDto?.metadata?.stationIds?.length &&
    rideInitialSnapshotDto?.status !== RideStatusEnum.Canceled;

  if (isSpecialCaseLastPassengerRemoved) {
    Log.debug(
      `Special case: last passenger removed from ride, ` +
        `initial ride stations count snapshot: ${rideInitialSnapshotDto?.metadata?.stationIds?.length ?? NO_VALUE_DISPLAY}`,
    );
  }

  const isSaveAndStayButtonShown = useMemo(() => {
    const { externalMetadata, status } = rideDto || {};

    const hasPermissions =
      hasRideUpdatePermission ||
      hasRideUpdateDriverPermission ||
      hasRideUpdateVehiclePermission;

    if (!hasPermissions) {
      return false;
    }

    if (status === RideStatusEnum.Draft) {
      return true;
    }

    return Object.keys(externalMetadata || {}).length === 0;
  }, [
    rideDto,
    hasRideUpdatePermission,
    hasRideUpdateDriverPermission,
    hasRideUpdateVehiclePermission,
  ]);

  const isSaveAndStayButtonDisabled = useMemo(() => {
    const { metadata } = rideDto || {};
    return (
      isInnerItemEditing ||
      isLoaderActive ||
      (!metadata?.stationIds?.length && !isSpecialCaseLastPassengerRemoved) ||
      hasEditNewOnlyRideRestriction
    );
  }, [
    rideDto,
    isInnerItemEditing,
    isLoaderActive,
    hasEditNewOnlyRideRestriction,
    isSpecialCaseLastPassengerRemoved,
  ]);

  const isDisableSendToSupplier = useMemo(() => {
    const { metadata, oldId, status } = rideDto ?? {};

    // RYDE-7633: a taxi provider rejects a past ride, so disable sending it — the ride must be
    // duplicated to a new supplier instead. (The backend also rejects the send as a safety net.)
    if (isPastTaxiSupplierRide(rideDto)) {
      return true;
    }

    if (isInnerItemEditing || hasEditNewOnlyRideRestriction) {
      return true;
    }

    if (isLoaderActive || !supplierId || !vehicleTypeId) {
      return true;
    }

    if (status === RideStatusEnum.Draft) {
      return true;
    }

    if (!allowSendingToSupplierWithoutCompanyId && !oldId) {
      return true;
    }

    if (!metadata?.stationIds?.length && !isSpecialCaseLastPassengerRemoved) {
      return true;
    }

    if (
      !isRideWithEmptyStationsAllowed &&
      !hasCourseId &&
      metadata?.stationIds?.length &&
      !metadata?.passengerIds?.length &&
      !isSpecialCaseLastPassengerRemoved
    ) {
      return true;
    }

    return false;
  }, [
    hasCourseId,
    hasEditNewOnlyRideRestriction,
    isInnerItemEditing,
    isLoaderActive,
    isRideWithEmptyStationsAllowed,
    isSpecialCaseLastPassengerRemoved,
    rideDto,
    supplierId,
    vehicleTypeId,
    allowSendingToSupplierWithoutCompanyId,
  ]);

  const isEditMode = useMemo(() => {
    return Boolean(rideId) && Boolean(isEdit);
  }, [isEdit, rideId]);

  const isCancelButtonDisplayed = useMemo(() => {
    return Boolean(rideId) && rideDto?.status !== RideStatusEnum.Canceled;
  }, [rideDto?.status, rideId]);

  // RYDE-7633: a past taxi ride (GETT/Yango/Kastel/ISR, already started) is locked read-only — the
  // only permitted actions are Cancel, Delete/Archive, and Duplicate to new supplier. Every other
  // footer action (save/send/renew/regular-duplicate/opposite/convert) is hidden.
  const isReadOnlyPastTaxi = useMemo(
    () => isPastTaxiSupplierRide(rideDto),
    [rideDto],
  );

  const isRenewButtonDisplayed = useMemo(() => {
    return (
      Boolean(rideId) &&
      !isReadOnlyPastTaxi &&
      rideDto?.status &&
      [RideStatusEnum.Canceled, RideStatusEnum.Rejected].includes(
        rideDto?.status,
      )
    );
  }, [rideDto?.status, rideId, isReadOnlyPastTaxi]);

  const isCreateOppositeRideButtonShown = useMemo(() => {
    return Boolean(rideId) && !isReadOnlyPastTaxi;
  }, [rideId, isReadOnlyPastTaxi]);

  const isDuplicateButtonDisplayed = useMemo(() => {
    return Boolean(rideId) && !isReadOnlyPastTaxi;
  }, [rideId, isReadOnlyPastTaxi]);

  // RYDE-7633: a past taxi ride can only be moved to a new supplier by duplicating it.
  const isDuplicateToNewSupplierShown = useMemo(
    () => Boolean(rideId) && isReadOnlyPastTaxi,
    [rideId, isReadOnlyPastTaxi],
  );

  const isSendToSupplierButtonDisplayed = useMemo(() => {
    return rideDto?.status !== RideStatusEnum.Rejected && !isReadOnlyPastTaxi;
  }, [rideDto?.status, isReadOnlyPastTaxi]);

  return (
    <RideFooterStyled
      className={clsx(
        "RideDrawer-Footer",
        isInnerItemEditing && "inner-item-editing",
      )}
    >
      <RideFooterLeftButtonsStyled>
        <AccessControl entity="ride" action="delete">
          {isEditMode && (
            <FramerTooltip content={t(`general.ARCHIVE`)}>
              <EditFormFooterButton
                onClick={onArchiveClickEvent}
                disabled={isLoaderActive}
              >
                <InventoryIcon className="ride-footer__icon" />
                <span>{t(`general.ARCHIVE`)}</span>
              </EditFormFooterButton>
            </FramerTooltip>
          )}
        </AccessControl>

        <AccessControl entity="ride" action="cancel">
          {isCancelButtonDisplayed && (
            <FramerTooltip content={t(`ride.CANCEL_RIDE`)}>
              <EditFormFooterButton
                onClick={onCancelledClickEvent}
                disabled={isLoaderActive}
              >
                <Cancel className="ride-footer__icon" />
                <span>{t(`ride.CANCEL_RIDE`)}</span>
              </EditFormFooterButton>
            </FramerTooltip>
          )}
        </AccessControl>

        <AccessControl entity="ride" action="renew">
          {isRenewButtonDisplayed && (
            <FramerTooltip content={t(`ride.RENEW_RIDE`)}>
              <EditFormFooterButton
                onClick={onRenewRidesClickEvent}
                disabled={isLoaderActive}
              >
                <Replay className="ride-footer__icon" />
                <span>{t(`ride.RENEW_RIDE`)}</span>
              </EditFormFooterButton>
            </FramerTooltip>
          )}
        </AccessControl>

        <AccessControl entity="ride" action="create">
          {isDuplicateButtonDisplayed && (
            <FramerTooltip content={t(`general.DUPLICATE`)}>
              <EditFormFooterButton
                onClick={onDuplicateClickEvent}
                disabled={isLoaderActive}
              >
                <ContentCopyIcon className="ride-footer__icon" />
                <span>{t(`general.DUPLICATE`)}</span>
              </EditFormFooterButton>
            </FramerTooltip>
          )}
          {isCreateOppositeRideButtonShown && (
            <FramerTooltip content={t(`ride.RETURN_RIDE`)}>
              <EditFormFooterButton
                onClick={onDuplicateRevertedRideClick}
                disabled={isLoaderActive}
              >
                <AddTask className="ride-footer__icon" />
                {!hasCourseId && <span>{t(`ride.RETURN_RIDE`)}</span>}
              </EditFormFooterButton>
            </FramerTooltip>
          )}
          {isDuplicateToNewSupplierShown && (
            <FramerTooltip content={t(`ride.DUPLICATE_TO_NEW_SUPPLIER`)}>
              <EditFormFooterButton
                onClick={onDuplicateToNewSupplierClick}
                disabled={isLoaderActive}
              >
                <SwapHoriz className="ride-footer__icon" />
                <span>{t(`ride.DUPLICATE_TO_NEW_SUPPLIER`)}</span>
              </EditFormFooterButton>
            </FramerTooltip>
          )}
        </AccessControl>

        <AccessControl entity="ride" action="update">
          {hasCourseId && !isReadOnlyPastTaxi && (
            <FramerTooltip content={t(`ride.CONVERT_TO_DYNAMIC_RIDE`)}>
              <IconButton
                onClick={onConvertToDynamicRideClick}
                disabled={isLoaderActive}
              >
                <PublishedWithChanges className="ride-footer__icon--large" />
              </IconButton>
            </FramerTooltip>
          )}
        </AccessControl>
      </RideFooterLeftButtonsStyled>

      <RideFooterRightButtonsStyled>
        <AccessControl entity="ride" action="send_to_supplier">
          {isSendToSupplierButtonDisplayed && (
            <FramerTooltip content={t(`general.SAVE_AND_SEND_TO_SUPPLIER`)}>
              <MuiButton
                loading={isLoaderActive}
                disabled={
                  isDisableSendToSupplier ||
                  isLoaderActive ||
                  isSendBlockedForSelectedTaxiPast
                }
                onClick={onSaveAndSendToSupplierHandler}
                width={140}
                muiStyle={"secondary"}
                sx={
                  isInnerItemEditing
                    ? {
                        border: "2px dashed",
                        borderColor: "warning.main",
                      }
                    : undefined
                }
              >
                {t(`general.SAVE_AND_SEND_TO_SUPPLIER`)}
              </MuiButton>
            </FramerTooltip>
          )}
        </AccessControl>
        {isSaveAndStayButtonShown && (
          <FramerTooltip content={t(`general.SAVE_AND_STAY`)}>
            <MuiButton
              loading={isLoaderActive}
              disabled={isSaveAndStayButtonDisabled}
              onClick={onSaveAndStayClickEvent}
              sx={
                isInnerItemEditing
                  ? {
                      border: "2px dashed",
                      borderColor: "warning.main",
                    }
                  : undefined
              }
            >
              {t(`general.SAVE_AND_STAY`)}
            </MuiButton>
          </FramerTooltip>
        )}
      </RideFooterRightButtonsStyled>
    </RideFooterStyled>
  );
};
